import React, { useState, useEffect, useRef } from 'react';
import { createAgent, checkUserExists } from '../services/agencyService';
import { XMarkIcon, UserPlusIcon, EyeIcon, EyeSlashIcon, CheckCircleIcon, PhoneIcon } from '@heroicons/react/24/outline';

const CreateAgentModal = ({ show, onClose, onSuccess, agencyId, agencyName }) => {
  const [formData, setFormData] = useState({
    given: '',
    surname: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Email-existence state: null = unknown, true/false = checked.
  const [emailExists, setEmailExists] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const emailCheckTimer = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }

    // Reset email-exists state whenever email is edited; debounce the lookup.
    if (name === 'email') {
      setEmailExists(null);
      if (emailCheckTimer.current) clearTimeout(emailCheckTimer.current);
      const trimmed = value.trim();
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        emailCheckTimer.current = setTimeout(() => runEmailExistsCheck(trimmed), 500);
      }
    }
  };

  const runEmailExistsCheck = async (email) => {
    setCheckingEmail(true);
    try {
      const result = await checkUserExists(email);
      setEmailExists(!!result.exists);
      // If a user exists, clear any password the inviter typed — the field is
      // disabled and the backend ignores it.
      if (result.exists) {
        setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
        setErrors(prev => ({ ...prev, password: null, confirmPassword: null }));
      }
    } catch (err) {
      // Soft-fail: don't block the inviter if the check fails — backend will
      // still validate. Reset to "unknown" so password field stays visible.
      setEmailExists(null);
    } finally {
      setCheckingEmail(false);
    }
  };

  useEffect(() => () => {
    if (emailCheckTimer.current) clearTimeout(emailCheckTimer.current);
  }, []);

  const validate = () => {
    const newErrors = {};

    if (!formData.given.trim()) newErrors.given = 'First name is required';
    if (!formData.surname.trim()) newErrors.surname = 'Last name is required';

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Password fields only required when the email does NOT match an existing user.
    if (emailExists !== true) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setApiError(null);
    setSuccessMessage(null);

    try {
      const payload = {
        given: formData.given,
        surname: formData.surname,
        email: formData.email.trim().toLowerCase(),
        mobile: formData.mobile || undefined,
      };
      // Only include password for new-user flow.
      if (emailExists !== true) {
        payload.password = formData.password;
      }

      const result = await createAgent(agencyId, payload);
      const message = result.resent
        ? `Invitation email re-sent to ${result.email}.`
        : `Invitation email sent to ${result.email}.`;
      setSuccessMessage(message);

      // Reset form but keep the modal open briefly so the inviter sees the
      // success message before the parent closes us via onSuccess.
      setFormData({ given: '', surname: '', email: '', mobile: '', password: '', confirmPassword: '' });
      setEmailExists(null);
      setTimeout(() => onSuccess(), 1500);
    } catch (err) {
      setApiError(err.message || 'Failed to invite agent');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ given: '', surname: '', email: '', mobile: '', password: '', confirmPassword: '' });
    setErrors({});
    setApiError(null);
    setSuccessMessage(null);
    setEmailExists(null);
    onClose();
  };

  // Password strength indicator
  const getPasswordStrength = () => {
    const password = formData.password;
    if (!password) return { strength: 0, label: '' };
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
    const colors = ['', 'bg-red-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
    return { strength, label: labels[strength], color: colors[strength] };
  };

  const passwordStrength = getPasswordStrength();
  const passwordFieldsDisabled = emailExists === true || loading;

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity animate-fade-in"
        onClick={handleClose}
      />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-scale-in">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
            <div className="flex items-center">
              <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 p-2.5 rounded-xl shadow-lg shadow-emerald-500/20">
                <UserPlusIcon className="h-5 w-5 text-white" />
              </div>
              <div className="ml-4">
                <h2 className="text-lg font-semibold text-gray-900">Add New Agent</h2>
                {agencyName && (
                  <p className="text-sm text-gray-500 mt-0.5">{agencyName}</p>
                )}
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            {apiError && (
              <div className="mb-5 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start space-x-3 animate-slide-down">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                  <span className="text-red-600 text-xs">!</span>
                </div>
                <p className="text-sm text-red-700">{apiError}</p>
              </div>
            )}

            {successMessage && (
              <div className="mb-5 p-4 bg-green-50 border border-green-100 rounded-xl flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                <p className="text-sm text-green-700">{successMessage}</p>
              </div>
            )}

            <div className="space-y-5">
              {/* Name Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="given"
                    value={formData.given}
                    onChange={handleChange}
                    className={`input ${errors.given ? 'input-error' : ''}`}
                    placeholder="John"
                  />
                  {errors.given && (
                    <p className="mt-1.5 text-xs text-red-600 flex items-center">
                      <span className="inline-block w-1 h-1 bg-red-500 rounded-full mr-1.5"></span>
                      {errors.given}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="surname"
                    value={formData.surname}
                    onChange={handleChange}
                    className={`input ${errors.surname ? 'input-error' : ''}`}
                    placeholder="Smith"
                  />
                  {errors.surname && (
                    <p className="mt-1.5 text-xs text-red-600 flex items-center">
                      <span className="inline-block w-1 h-1 bg-red-500 rounded-full mr-1.5"></span>
                      {errors.surname}
                    </p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`input ${errors.email ? 'input-error' : ''}`}
                  placeholder="agent@example.com"
                />
                {errors.email && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center">
                    <span className="inline-block w-1 h-1 bg-red-500 rounded-full mr-1.5"></span>
                    {errors.email}
                  </p>
                )}
                {checkingEmail && (
                  <p className="mt-1.5 text-xs text-gray-500">Checking if a user with this email already exists…</p>
                )}
                {emailExists === true && (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800">
                      A user with this email already exists. They will be added as an agent
                      for <span className="font-semibold">{agencyName || 'this agency'}</span> after
                      confirming via the verification link sent to their email. No new password
                      will be created — the existing user keeps their current credentials.
                      Proceed?
                    </p>
                  </div>
                )}
              </div>

              {/* Mobile Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Mobile Phone <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    className="input input-with-icon"
                    placeholder="+61 412 345 678"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password {emailExists !== true && <span className="text-red-500">*</span>}
                  {emailExists === true && (
                    <span className="ml-1 text-xs font-normal text-gray-500">(disabled — existing user)</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={passwordFieldsDisabled}
                    className={`input pr-10 ${errors.password ? 'input-error' : ''} ${passwordFieldsDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder={emailExists === true ? '— not required —' : 'Minimum 8 characters'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={passwordFieldsDisabled}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40"
                  >
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                {/* Password Strength Indicator */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                          style={{ width: `${passwordStrength.strength * 25}%` }}
                        />
                      </div>
                      <span className={`text-xs font-medium ${
                        passwordStrength.strength <= 1 ? 'text-red-600' :
                        passwordStrength.strength === 2 ? 'text-yellow-600' :
                        passwordStrength.strength === 3 ? 'text-blue-600' : 'text-green-600'
                      }`}>
                        {passwordStrength.label}
                      </span>
                    </div>
                  </div>
                )}
                {errors.password && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center">
                    <span className="inline-block w-1 h-1 bg-red-500 rounded-full mr-1.5"></span>
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Confirm Password {emailExists !== true && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={passwordFieldsDisabled}
                    className={`input pr-10 ${errors.confirmPassword ? 'input-error' : ''} ${passwordFieldsDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder={emailExists === true ? '— not required —' : 'Re-enter password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={passwordFieldsDisabled}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40"
                  >
                    {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                {formData.confirmPassword && formData.password === formData.confirmPassword && (
                  <p className="mt-1.5 text-xs text-green-600 flex items-center">
                    <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                    Passwords match
                  </p>
                )}
                {errors.confirmPassword && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center">
                    <span className="inline-block w-1 h-1 bg-red-500 rounded-full mr-1.5"></span>
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            {/* Info Note */}
            <div className="mt-5 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl">
              <p className="text-sm text-blue-700">
                <span className="font-medium">Note:</span> Submitting this form sends a
                verification email with a one-time link. The invitee becomes an agent
                of this agency only after they click the link.
              </p>
            </div>

            {/* Footer */}
            <div className="mt-6 flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg hover:from-emerald-600 hover:to-emerald-700 shadow-lg shadow-emerald-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Sending invitation…
                  </>
                ) : (
                  <>
                    <UserPlusIcon className="h-4 w-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateAgentModal;
